import { Controller } from "@hotwired/stimulus";

export default class PopOutController extends Controller<HTMLElement> {
  static targets = ["content", "close", "footer"];

  private lastActiveElement: HTMLElement | null = null;
  private footerResizeObserver: ResizeObserver | null = null;
  private mouseDownInsideModal = false;

  declare readonly contentTarget: HTMLElement;
  declare readonly closeTarget: HTMLElement;
  declare readonly closeTargets: HTMLElement[];
  declare readonly footerTarget: HTMLElement;

  connect() {
    document.querySelector("body")?.classList.add("puts-pop-out-is-open-body");

    this.lastActiveElement = document.activeElement as HTMLElement;
    this.contentTarget.setAttribute("tabindex", "-1");
    this.contentTarget.focus();

    document.addEventListener("mousedown", this.eventMouseDown);
    document.addEventListener("click", this.eventClickOutside);
    document.addEventListener("keydown", this.eventCloseKeydown);
  }
  disconnect() {
    document
      .querySelector("body")
      ?.classList.remove("puts-pop-out-is-open-body");

    document.removeEventListener("mousedown", this.eventMouseDown);
    document.removeEventListener("click", this.eventClickOutside);
    document.removeEventListener("keydown", this.eventCloseKeydown);
  }
  closeTargetConnected(target: HTMLElement) {
    target.addEventListener("click", this.eventCloseClick);
  }
  closeTargetDisconnected(target: HTMLElement) {
    target.removeEventListener("click", this.eventCloseClick);
  }

  footerTargetConnected(target: HTMLElement) {
    this.footerResizeObserver = new ResizeObserver(this.eventFooterChanges);
    this.footerResizeObserver.observe(target);
  }
  footerTargetDisconnected() {
    this.footerResizeObserver?.disconnect();
    this.footerResizeObserver = null;
  }

  public close() {
    const shouldClose = this.element.dispatchEvent(
      new Event("puts-pop-out:before-close", {
        cancelable: true,
        bubbles: true,
      })
    );

    if (shouldClose) {
      this.lastActiveElement?.focus();
      this.element.remove();
    }
  }

  private eventCloseClick = (event: PointerEvent) => {
    event.preventDefault();
    this.close();
  };
  private eventMouseDown = (event: MouseEvent) => {
    this.mouseDownInsideModal = this.element.contains(
      event.target as HTMLElement
    );
  };
  private eventClickOutside = (event: PointerEvent) => {
    // Ignore clicks that started (mousedown) inside the modal — this prevents
    // the modal from closing when the user drags from inside to outside and releases.
    if (this.mouseDownInsideModal) return;

    const target = event.target as HTMLElement;
    if (
      !this.element.contains(target) &&
      target.dataset.putsPopOutIgnoreClose !== "true" &&
      document.body.contains(target) &&
      !target.closest("[data-base-ui-portal]")
    ) {
      event.preventDefault();
      this.close();
    }
  };
  private eventCloseKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
    }
  };
  private eventFooterChanges = () => {
    this.element.style.setProperty(
      "--puts-pop-out-footer-height",
      `${this.footerTarget.clientHeight}px`
    );
  };
}
